<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AssistantAPI from 'dashboard/api/assistant';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AssistantPageLayout from '../components/AssistantPageLayout.vue';
import { usePersistentRef } from '../composables/useAssistantCache';

const { t } = useI18n();

// Survives navigating away and back; { role, content, confident?, sources? }
const messages = usePersistentRef('playground-messages', []);
const input = ref('');
const isWaiting = ref(false);
const errorText = ref('');
const messageContainer = ref(null);

const history = computed(() =>
  messages.value.map(({ role, content }) => ({ role, content }))
);

const isUserMessage = role => role === 'user';

const getAvatarName = role =>
  isUserMessage(role)
    ? t('ASSISTANT.PLAYGROUND.USER')
    : t('ASSISTANT.PLAYGROUND.ASSISTANT');

const getMessageStyle = role =>
  isUserMessage(role)
    ? 'bg-n-solid-blue border-n-blue-7/40'
    : 'bg-n-surface-1 border-n-weak';

const scrollToBottom = async () => {
  await nextTick();
  if (messageContainer.value) {
    messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
  }
};

watch(() => messages.value.length, scrollToBottom);

const resetConversation = () => {
  messages.value = [];
  input.value = '';
  errorText.value = '';
};

const send = async () => {
  const question = input.value.trim();
  if (!question || isWaiting.value) return;
  errorText.value = '';
  const priorHistory = history.value;
  messages.value.push({ role: 'user', content: question });
  input.value = '';
  isWaiting.value = true;
  try {
    const { data } = await AssistantAPI.playground({
      message: question,
      history: priorHistory,
    });
    messages.value.push({
      role: 'assistant',
      content: data.reply,
      confident: data.confident,
      sources: data.sources,
    });
  } catch (error) {
    errorText.value =
      error.response?.status === 429
        ? t('ASSISTANT.PLAYGROUND.LIMIT_ERROR')
        : t('ASSISTANT.PLAYGROUND.ERROR');
  } finally {
    isWaiting.value = false;
  }
};

const handleEnterKey = event => {
  if (event.isComposing) return;
  event.preventDefault();
  if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return;
  send();
};
</script>

<template>
  <AssistantPageLayout :header-title="t('ASSISTANT.PLAYGROUND.TITLE')">
    <template #actions>
      <Button
        ghost
        slate
        size="sm"
        icon="i-lucide-rotate-ccw"
        class="!h-11 !w-11 !rounded-md"
        :aria-label="t('ASSISTANT.PLAYGROUND.RESET')"
        :disabled="isWaiting"
        @click="resetConversation"
      />
    </template>

    <div
      class="flex flex-col flex-1 min-h-0 overflow-hidden border rounded-lg border-n-weak bg-n-surface-1"
    >
      <div class="shrink-0 border-b border-n-weak px-4 py-4 sm:px-6">
        <p class="mb-0 text-xs leading-6 text-n-slate-11">
          {{ t('ASSISTANT.PLAYGROUND.DESCRIPTION') }}
        </p>
      </div>

      <div
        ref="messageContainer"
        class="min-h-0 flex-1 px-4 py-6 space-y-5 overflow-y-auto bg-n-background sm:px-6"
      >
        <div
          v-if="!messages.length && !isWaiting"
          class="flex flex-col items-center justify-center h-full gap-4 px-4 text-center text-sm leading-6 text-n-slate-11"
        >
          <span
            class="grid size-12 place-items-center rounded-full bg-n-blue-3 text-n-blue-11"
            aria-hidden="true"
          >
            <span class="i-ph-sparkle-fill size-5" />
          </span>
          <p class="mb-0 max-w-sm">{{ t('ASSISTANT.PLAYGROUND.EMPTY') }}</p>
        </div>

        <div
          v-for="(message, index) in messages"
          :key="index"
          class="flex"
          :class="isUserMessage(message.role) ? 'justify-end' : 'justify-start'"
        >
          <div
            class="flex min-w-0 items-end gap-2 max-w-[95%] md:max-w-[75%]"
            :class="
              isUserMessage(message.role) ? 'flex-row-reverse' : 'flex-row'
            "
          >
            <Avatar
              :name="getAvatarName(message.role)"
              rounded-full
              :size="24"
              class="shrink-0"
            />
            <div
              class="min-w-0 rounded-lg border px-4 py-3 text-sm leading-7 text-n-slate-12 [overflow-wrap:anywhere]"
              :class="getMessageStyle(message.role)"
            >
              <p class="mb-0 whitespace-pre-wrap">{{ message.content }}</p>
              <template v-if="message.role === 'assistant'">
                <p
                  class="mt-2 text-xs font-medium"
                  :class="
                    message.confident ? 'text-n-teal-11' : 'text-n-amber-11'
                  "
                >
                  {{
                    message.confident
                      ? t('ASSISTANT.PLAYGROUND.CONFIDENT')
                      : t('ASSISTANT.PLAYGROUND.HANDOFF')
                  }}
                </p>
                <details
                  v-if="message.sources?.length"
                  class="mt-1 text-xs text-n-slate-11"
                >
                  <summary
                    class="cursor-pointer py-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-8"
                  >
                    {{ t('ASSISTANT.PLAYGROUND.SOURCES') }}
                  </summary>
                  <ul class="mt-1 list-disc ps-4">
                    <li
                      v-for="source in message.sources"
                      :key="source.source_ref"
                    >
                      {{ source.source_ref }}
                    </li>
                  </ul>
                </details>
              </template>
            </div>
          </div>
        </div>

        <div v-if="isWaiting" class="flex justify-start">
          <div class="flex items-start gap-1.5">
            <Avatar
              :name="getAvatarName('assistant')"
              rounded-full
              :size="24"
            />
            <div
              class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 p-3 text-xs text-n-slate-11"
              role="status"
            >
              <span
                class="i-lucide-loader-circle size-4 shrink-0 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              {{ t('ASSISTANT.PLAYGROUND.THINKING') }}
            </div>
          </div>
        </div>

        <p v-if="errorText" class="text-sm text-n-ruby-11" role="alert">
          {{ errorText }}
        </p>
      </div>

      <form
        class="flex shrink-0 items-center gap-2 border-t border-n-weak bg-n-surface-1 p-3 focus-within:border-n-blue-8 sm:px-4"
        @submit.prevent="send"
      >
        <input
          v-model="input"
          class="min-w-0 flex-1 mb-0 text-sm bg-transparent border-none focus:outline-none text-n-slate-12 placeholder:text-n-slate-11"
          :placeholder="t('ASSISTANT.PLAYGROUND.INPUT_PLACEHOLDER')"
          :aria-label="t('ASSISTANT.PLAYGROUND.INPUT_PLACEHOLDER')"
          @keydown.enter="handleEnterKey"
        />
        <Button
          type="submit"
          size="sm"
          :disabled="!input.trim() || isWaiting"
          icon="i-lucide-send"
          class="!h-11 !w-11 !rounded-md shrink-0"
          :aria-label="t('ASSISTANT.PLAYGROUND.SEND')"
        />
      </form>
    </div>
  </AssistantPageLayout>
</template>
